<script lang="ts">
	import { onMount, onDestroy } from 'svelte';

	// Props
	let {
		value = '',
		language = 'javascript',
		theme = 'vs-dark',
		readOnly = true,
		onChange = (value: string) => {}
	}: {
		value?: string;
		language?: string;
		theme?: string;
		readOnly?: boolean;
		onChange?: (value: string) => void;
	} = $props();

	let editorContainer: HTMLDivElement;
	let editor: any = null;
	let monacoLoaded = $state(false);
	let loadError = $state('');

	// Load Monaco from CDN
	function loadMonaco() {
		return new Promise((resolve, reject) => {
			// Check if already loaded
			if ((window as any).monaco) {
				monacoLoaded = true;
				resolve((window as any).monaco);
				return;
			}

			// Load Monaco CSS
			const cssLink = document.createElement('link');
			cssLink.rel = 'stylesheet';
			cssLink.href = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs/editor/editor.main.css';
			document.head.appendChild(cssLink);

			// Configure AMD loader
			const loaderScript = document.createElement('script');
			loaderScript.src = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs/loader.js';
			loaderScript.onload = () => {
				(window as any).require.config({
					paths: {
						vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs'
					}
				});

				(window as any).require(['vs/editor/editor.main'], () => {
					monacoLoaded = true;
					resolve((window as any).monaco);
				});
			};
			loaderScript.onerror = () => {
				reject(new Error('Failed to load Monaco Editor'));
			};
			document.head.appendChild(loaderScript);
		});
	}

	onMount(async () => {
		if (!editorContainer) {
			console.error('Monaco: editorContainer not found');
			return;
		}

		try {
			// Load Monaco from CDN
			const monaco = await loadMonaco();

			if (!monaco) {
				loadError = 'Failed to load Monaco Editor';
				return;
			}

			// Create the editor
			editor = monaco.editor.create(editorContainer, {
				value: value,
				language: language,
				theme: theme,
				readOnly: readOnly,
				automaticLayout: true,
				minimap: { enabled: true },
				fontSize: 14,
				lineNumbers: 'on',
				scrollBeyondLastLine: false,
				wordWrap: 'on',
				folding: true,
				renderWhitespace: 'selection',
				tabSize: 2,
				insertSpaces: true,
			});

			console.log('Monaco Editor initialized successfully');

			// Listen for content changes
			if (!readOnly) {
				editor.onDidChangeModelContent(() => {
					const newValue = editor?.getValue() || '';
					onChange(newValue);
				});
			}
		} catch (error) {
			console.error('Failed to initialize Monaco Editor:', error);
			loadError = 'Failed to initialize editor';
		}
	});

	// Update editor value when prop changes
	$effect(() => {
		if (editor && editor.getValue() !== value && monacoLoaded) {
			editor.setValue(value);
		}
	});

	// Update editor language when prop changes
	$effect(() => {
		if (editor && monacoLoaded && (window as any).monaco) {
			const model = editor.getModel();
			if (model) {
				(window as any).monaco.editor.setModelLanguage(model, language);
			}
		}
	});

	// Update editor theme when prop changes
	$effect(() => {
		if (editor && monacoLoaded && (window as any).monaco) {
			(window as any).monaco.editor.setTheme(theme);
		}
	});

	onDestroy(() => {
		if (editor) {
			editor.dispose();
		}
	});
</script>

{#if loadError}
	<div class="monaco-editor-error">
		<p class="text-red-600">{loadError}</p>
	</div>
{:else if !monacoLoaded}
	<div class="monaco-editor-loading">
		<div class="flex items-center justify-center h-full">
			<div class="text-center">
				<div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
				<p class="mt-4 text-gray-600">Loading editor...</p>
			</div>
		</div>
	</div>
{/if}

<div bind:this={editorContainer} class="monaco-editor-container" class:hidden={!monacoLoaded || loadError}></div>

<style>
	.monaco-editor-container {
		width: 100%;
		height: 100%;
		min-height: 400px;
	}

	.monaco-editor-loading,
	.monaco-editor-error {
		width: 100%;
		height: 100%;
		min-height: 400px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
</style>
